import { omitBy, findKey, isNumber } from 'lodash';
import {
  EnhancedBillingPage,
  PageBillingType,
} from '../interfaces/connected-page';
import {
  DefaultPlansFeatures,
  FullFeatures,
  NoFeatures,
  PlanFeatures,
  PlansMapping,
  PlanTypeBE,
  AllotmentDefinition,
  EnhancedPlan,
} from '../interfaces/subscription';
import PromotionModel from '../model/PromotionModel';
import { SubscriptionModel } from '../model/SubscriptionModel';
import ConnectedPageModel from '../model/ConnectedPageModel';

export type PlanInfo = {
  isFreePlan: boolean;
  isProPlan: boolean;
  isLegacyPlan: boolean;
  isLegacyProPlan: boolean;
  isLegacyTeamPlan: boolean;
  isPromoPlan: boolean;
  isNewTeamPlan: boolean;
  isAnyTeamPlan: boolean;
  isAnyProPlan: boolean;
  isProUnicorn: boolean;
  isSpecialPlan: boolean;
  isFreeOrSpecialPlan: boolean;
  isCustomPro: boolean;
  isCustomTeam: boolean;
  isInstachamp: boolean;
  isTeam2020: boolean;
  isSpecialPro: boolean;
  isSpecialTeam: boolean;
  isTrialTeam: boolean;
};

export type PlanFeaturedInfo = {
  info: Partial<PlanInfo>;
  features: PlanFeatures;
};

const isFalsy = (value: any) => !(Boolean(value) || value === 0);

const combineFeatures = (features: PlanFeatures[]): PlanFeatures => {
  return features.reduce((feature, acc) => {
    return { ...acc, ...omitBy(feature, isFalsy) };
  }, NoFeatures);
};

const getPlanFeatures = (
  plansMapping?: PlansMapping | null,
  subscriptions?: SubscriptionModel[],
  redemption?: PromotionModel,
): PlanFeatures => {
  if (redemption) {
    return FullFeatures;
  }
  if (subscriptions && subscriptions.length > 0) {
    if (
      subscriptions.find(
        sub =>
          sub.planType === PlanTypeBE.team || sub.planType === PlanTypeBE.pro,
      )
    ) {
      return FullFeatures;
    }
    return combineFeatures(
      subscriptions.map(sub => {
        const code = sub.planCode;
        const key =
          plansMapping && findKey(plansMapping, planCode => planCode === code);
        return (
          (key && DefaultPlansFeatures[key as keyof PlansMapping]) || NoFeatures
        );
      }),
    );
  }
  return NoFeatures;
};

export const getPlanInfoWithFeatures = (
  mapping?: PlansMapping,
  page?: EnhancedBillingPage,
): PlanFeaturedInfo => {
  if (!page) {
    return { info: {}, features: NoFeatures };
  }
  const planInfo = getPlanInfoFromBillingType(page.pageBillingType, page);
  const planFeatures = getPlanFeatures(
    mapping,
    page.subscriptions,
    page.redemption,
  );
  return { info: planInfo, features: planFeatures };
};

export const getPlanInfo = (
  page?: EnhancedBillingPage,
  billingType?: PageBillingType,
): Partial<PlanInfo> => {
  if (!page && !billingType) {
    return {};
  }
  const pageBillingType = page && page.pageBillingType;

  return getPlanInfoFromBillingType(
    billingType || (pageBillingType as PageBillingType),
    page,
  );
};

export const getPlanInfoFromBillingType = (
  pageBillingType: PageBillingType,
  page?: EnhancedBillingPage | ConnectedPageModel,
): PlanInfo => {
  const isFreePlan = pageBillingType === PageBillingType.Free;
  const isProPlan = pageBillingType === PageBillingType.Pro;
  const isLegacyProPlan = pageBillingType === PageBillingType.LegacyPro;
  const isLegacyTeamPlan = pageBillingType === PageBillingType.LegacyTeam;
  const isNewTeamPlan = pageBillingType === PageBillingType.Team;
  const isTrialTeam = pageBillingType === PageBillingType.TrialTeam;
  const isPromoPlan =
    pageBillingType === PageBillingType.Promotion ||
    isTrialTeam ||
    Boolean(page && page.redemption);
  const isProUnicorn = pageBillingType === PageBillingType.ProUnicorn;
  const isLegacyPlan = isLegacyProPlan || isLegacyTeamPlan || isPromoPlan;
  const isSpecialTeam = pageBillingType === PageBillingType.SpecialTeam;
  const isSpecialPro =
    pageBillingType === PageBillingType.SpecialPro ||
    pageBillingType === PageBillingType.WP ||
    pageBillingType === PageBillingType.Instachamp;
  const isInstachamp = pageBillingType === PageBillingType.Instachamp;
  const isCustomTeam = pageBillingType === PageBillingType.CustomTeam;
  const isCustomPro = pageBillingType === PageBillingType.CustomPro;
  const isTeam2020 = pageBillingType === PageBillingType.NewTeam;

  return {
    isCustomTeam,
    isCustomPro,
    isFreePlan,
    isProPlan,
    isPromoPlan,
    isLegacyProPlan,
    isNewTeamPlan,
    isLegacyTeamPlan,
    isProUnicorn,
    isLegacyPlan,
    isTeam2020,
    isSpecialPro,
    isSpecialTeam,
    isTrialTeam,
    isInstachamp,
    isSpecialPlan: isSpecialTeam || isSpecialPro,
    isAnyProPlan:
      isProPlan ||
      isProUnicorn ||
      isCustomPro ||
      isSpecialPro ||
      isLegacyProPlan,
    isAnyTeamPlan:
      isNewTeamPlan ||
      isLegacyTeamPlan ||
      isCustomTeam ||
      isSpecialTeam ||
      isTeam2020 ||
      isTrialTeam,
    isFreeOrSpecialPlan:
      isFreePlan || isSpecialPro || (isSpecialTeam && !isPromoPlan),
  };
};

export const mapPlanAllotmentOptions = (args: {
  plansAllotments: {
    [key: string]: AllotmentDefinition;
  };
  getExtraAllotment: boolean;
  page: EnhancedBillingPage;
  plan: EnhancedPlan[];
  useFiltered: boolean;
  currentSendsLevel?: number;
}) => {
  const {
    plansAllotments,
    getExtraAllotment,
    page,
    plan,
    useFiltered,
    currentSendsLevel,
  } = args;

  const {
    isProUnicorn,
    isProPlan,
    isAnyTeamPlan,
    isInstachamp,
    isTeam2020,
    isCustomTeam,
    isLegacyTeamPlan,
    isTrialTeam,
  } = getPlanInfo(page, page.pageBillingType);

  let planAllotment;

  if (isProPlan) {
    planAllotment = plansAllotments.pro;
  }
  if (isProUnicorn) {
    planAllotment = plansAllotments.unicorn;
  }
  if (isTeam2020) {
    planAllotment = plansAllotments.team2020;
  }
  if (isCustomTeam || isLegacyTeamPlan) {
    planAllotment = plansAllotments.teamCustom;
  }
  if (isAnyTeamPlan) {
    planAllotment = plansAllotments.team;
  }
  if (isInstachamp) {
    planAllotment = plansAllotments.instachamp;
  }

  if (!planAllotment) {
    return [];
  }
  const { start, msgPerLevel, levels } = planAllotment;
  let planAllotmentLevel = levels + 1;
  if (
    !isNumber(planAllotment.levels) &&
    isAnyTeamPlan &&
    !isTrialTeam &&
    plan
  ) {
    planAllotmentLevel = plan[0].maxDisplayingSendAllotmentLevel;
  }

  const allotmentsArray = useFiltered
    ? [...Array(planAllotmentLevel).keys()].filter(
        (level: number) => level > Number(currentSendsLevel),
      )
    : [...Array(planAllotmentLevel).keys()];

  return allotmentsArray.map((level: number, index) => {
    const totalAllotmentNumber = start + msgPerLevel * level;
    const extraAllotmentNumber = msgPerLevel * level;
    return {
      id: index,
      name: getExtraAllotment
        ? `${extraAllotmentNumber}`
        : `${totalAllotmentNumber}`,
      level,
    };
  });
};

export const getPlanAllotmentAndMaxLevelByPlanInfo = ({
  plansAllotments,
  plan,
  page,
}: {
  plansAllotments: {
    [key: string]: AllotmentDefinition;
  };
  plan?: EnhancedPlan;
  page?: EnhancedBillingPage;
}): { planAllotment: AllotmentDefinition; maxSendsLevelForPlan: number } => {
  const {
    isProUnicorn,
    isProPlan,
    isAnyTeamPlan,
    isInstachamp,
    isTeam2020,
    isCustomTeam,
    isLegacyTeamPlan,
  } = getPlanInfo(page, page?.pageBillingType);

  let planAllotment;

  if (isProPlan) {
    planAllotment = plansAllotments.pro;
  }
  if (isProUnicorn) {
    planAllotment = plansAllotments.unicorn;
  }
  if (isTeam2020) {
    planAllotment = plansAllotments.team2020;
  }
  if (isCustomTeam || isLegacyTeamPlan) {
    planAllotment = plansAllotments.teamCustom;
  }
  if (isAnyTeamPlan && !(isTeam2020 || isCustomTeam || isLegacyTeamPlan)) {
    planAllotment = plansAllotments.team;
  }
  if (isInstachamp) {
    planAllotment = plansAllotments.instachamp;
  }

  const maxSendsLevelForPlan = isAnyTeamPlan
    ? plan?.maxDisplayingSendAllotmentLevel
    : planAllotment?.levels;

  return { planAllotment, maxSendsLevelForPlan };
};
